var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createStore } from "redux";
import { ReduceableReducer } from "../reducers/reduceable.reducer";
import { InterfaceUtil } from "../utils/interface.util";
import { freeze } from "../utils/object.util";
export class StoreManager {
    constructor(entries, storage, clearStorage = false) {
        var _a, _b;
        this.storage = localStorage;
        this.storageKey = "_redux_state_";
        this.storageState = {};
        this.initialStorageState = {};
        this.beforeUnloadListener = undefined;
        this.reducers = new Map();
        this.readyMap = new Map();
        this.storage = storage || this.storage;
        if (clearStorage === true) {
            this.storage.removeItem(this.storageKey);
        }
        let persistedState = this.storage.getItem(this.storageKey);
        if (persistedState != null) {
            this.storageState = JSON.parse(persistedState);
            this.initialStorageState = JSON.parse(persistedState);
            for (const key in Object.getOwnPropertyNames(this.initialStorageState)) {
                freeze(this.initialStorageState[key]);
            }
            this.storage.removeItem(this.storageKey);
        }
        for (const [key, value] of entries) {
            this.addReducer(key, value);
        }
        this.store = createStore(this.reduce.bind(this), (_b = (_a = window) === null || _a === void 0 ? void 0 : _a.__REDUX_DEVTOOLS_EXTENSION__) === null || _b === void 0 ? void 0 : _b.call(_a));
        this.beforeUnloadListener = this.handleBeforeUnload.bind(this);
        window.addEventListener("beforeunload", this.beforeUnloadListener);
    }
    static getInstance(config, storage, clearStorage = false) {
        let reducers = StoreManager.__normalizeConfig(config);
        let instance = StoreManager.instance;
        if (instance == null) {
            instance = StoreManager.instance = new StoreManager(reducers.entries(), storage, clearStorage);
        }
        else if (config != null) {
            StoreManager.instance.__reconfigure(reducers);
        }
        return instance;
    }
    static __normalizeConfig(config = {}) {
        let reducers;
        if (config instanceof Map) {
            reducers = config;
        }
        else {
            reducers = new Map();
            for (const [key, value] of Object.entries(config)) {
                reducers.set(key, value);
            }
        }
        return reducers;
    }
    __reconfigure(config) {
        for (const key of this.reducers.keys()) {
            if (!config.has(key)) {
                this.removeReducer(key);
            }
        }
        for (const [key, value] of config.entries()) {
            if (!this.reducers.has(key)) {
                this.addReducer(key, value);
            }
        }
    }
    static dispose() {
        if (StoreManager.instance != null) {
            StoreManager.instance.__dispose();
        }
        StoreManager.instance = undefined;
    }
    __dispose() {
        if (this.beforeUnloadListener != null) {
            window.removeEventListener("beforeunload", this.beforeUnloadListener);
            this.beforeUnloadListener = undefined;
        }
        this.dehydrate();
    }
    getStore() {
        return this.store;
    }
    isReady(slice) {
        return slice ? !this.readyMap.has(slice) : this.readyMap.size === 0;
    }
    waitUntilReady(slice) {
        return __awaiter(this, void 0, void 0, function* () {
            if (slice) {
                if (this.readyMap.has(slice)) {
                    yield this.readyMap.get(slice);
                }
            }
            else {
                yield Promise.all(this.readyMap.values());
            }
        });
    }
    getSlices() {
        return [...this.reducers.keys()];
    }
    getState(key) {
        var _a, _b, _c;
        return key ? (_b = (_a = this.store) === null || _a === void 0 ? void 0 : _a.getState()) === null || _b === void 0 ? void 0 : _b[key] : (_c = this.store) === null || _c === void 0 ? void 0 : _c.getState();
    }
    dispatch(action) {
        return action instanceof Promise
            ? this.dispatchAsync(action)
            : this.dispatchSync(action);
    }
    dispatchAsync(actionPromise) {
        return __awaiter(this, void 0, void 0, function* () {
            const action = yield actionPromise;
            this.dispatchSync(action);
        });
    }
    dispatchSync(action) {
        var _a;
        if (InterfaceUtil.isReduceableAction(action)) {
            if (!this.reducers.has(action.slice)) {
                this.addReducer(action.slice, new ReduceableReducer({}));
            }
            const reducer = this.reducers.get(action.slice);
            if (!(reducer === null || reducer === void 0 ? void 0 : reducer.actions.has(action.type))) {
                reducer === null || reducer === void 0 ? void 0 : reducer.actions.set(action.type, action.reduce);
            }
        }
        const pojo = JSON.parse(JSON.stringify(action));
        (_a = this.store) === null || _a === void 0 ? void 0 : _a.dispatch(pojo);
    }
    reduce(state, action) {
        var _a;
        const nextState = JSON.parse(JSON.stringify(state || {}));
        if ((_a = action.type) === null || _a === void 0 ? void 0 : _a.startsWith("...")) {
            nextState[action.slice] = action.state;
            return nextState;
        }
        if (InterfaceUtil.isSliceAction(action)) {
            const slice = action.slice;
            const reducer = this.reducers.get(slice);
            if (reducer != null) {
                this.reduceSlice(nextState, slice, reducer, action);
            }
            else {
                delete nextState[slice];
            }
        }
        else {
            for (const [slice, reducer] of this.reducers) {
                this.reduceSlice(nextState, slice, reducer, action);
            }
        }
        return nextState;
    }
    reduceSlice(nextState, slice, reducer, action) {
        nextState[slice] = reducer.reduce(nextState[slice], action);
        if (InterfaceUtil.isHydratableReducer(reducer) &&
            this.storageState[slice] != null) {
            try {
                const rehydrationResult = reducer.rehydrate(JSON.parse(JSON.stringify(nextState[slice])), this.storageState[slice], this.initialStorageState, this);
                if (rehydrationResult instanceof Promise) {
                    this.lazyRehydrate(rehydrationResult, action.type, slice);
                }
                else {
                    nextState[slice] = rehydrationResult;
                }
                delete this.storageState[slice];
            }
            catch (e) { }
        }
    }
    lazyRehydrate(promise, type, slice) {
        const readyPromise = new Promise((resolve, reject) => {
            promise
                .then((futureStateSlice) => {
                this.readyMap.delete(slice);
                this.dispatchSync({
                    type: `...${type}`,
                    slice,
                    state: futureStateSlice,
                });
                resolve(futureStateSlice);
            })
                .catch((error) => {
                this.readyMap.delete(slice);
                resolve(error);
            });
        });
        this.readyMap.set(slice, readyPromise);
    }
    addReducer(slice, reducer) {
        var _a;
        if (this.reducers.has(slice)) {
            return;
        }
        this.reducers.set(slice, reducer);
        (_a = this.store) === null || _a === void 0 ? void 0 : _a.dispatch({
            type: "@@REDUCER_ADD",
            key: slice,
        });
    }
    removeReducer(slice) {
        var _a;
        const reducer = this.reducers.get(slice);
        if (reducer == null) {
            return;
        }
        if (InterfaceUtil.isHydratableReducer(reducer)) {
            this.storageState[slice] = reducer.dehydrate(this.getState(slice), this);
            this.initialStorageState[slice] = JSON.parse(JSON.stringify(this.storageState[slice]));
            freeze(this.initialStorageState[slice]);
        }
        this.reducers.delete(slice);
        (_a = this.store) === null || _a === void 0 ? void 0 : _a.dispatch({ type: "@@REDUCER_REMOVE", slice });
    }
    suspendStorage() {
        this.storage.setItem(this.storageKey, "{}");
    }
    handleBeforeUnload(ev) {
        delete ev["returnValue"];
        this.dehydrate();
    }
    dehydrate() {
        if (this.storage.getItem(this.storageKey) != null) {
            this.storage.removeItem(this.storageKey);
            return;
        }
        const state = this.getState();
        for (const [slice, reducer] of this.reducers) {
            if (InterfaceUtil.isHydratableReducer(reducer)) {
                try {
                    this.storageState[slice] = reducer.dehydrate(state[slice], this);
                }
                catch (e) { }
            }
        }
        this.storage.setItem(this.storageKey, JSON.stringify(this.storageState));
    }
}
